/**
 * useInlineMassUpdate - Enables mass updates via inline cell editing
 *
 * When a user has multiple rows selected and edits a cell inline,
 * this hook applies that change to ALL selected rows automatically.
 *
 * Benefits:
 * - No separate mass update UI needed
 * - Users can leverage familiar inline editing for batch operations
 * - Reduces UI complexity while maintaining full functionality
 *
 * Usage:
 * ```tsx
 * const { processRowUpdate } = useInlineMassUpdate({
 *   entityType: 'organization',
 *   context: 'organizations',
 *   rowSelectionModel,
 *   apiRef,
 *   editableFields: ['status', 'team.AM', 'team.PM', 'contractedBy'],
 *   baseProcessRowUpdate: existingHandler, // optional - for additional processing
 * })
 *
 * <DataGridPremium processRowUpdate={processRowUpdate} />
 * ```
 */

import { useCallback, useRef } from 'react'
import { GridRowSelectionModel, GridApiPremium } from '@mui/x-data-grid-premium'
import { MutableRefObject } from 'react'
import { useMassUpdate } from './useMassUpdate'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'

// Entity type configuration (must match useMassUpdate)
type EntityType = 'organization' | 'deal'
type GridContext = 'organizations' | 'mtb' | 'deals' | 'nco' | 'products'

/** Resolver data for building display-ready grid updates */
interface ResolverOptions {
  users?: any[]
  contacts?: any[]
  organizations?: any[]
}

interface UseInlineMassUpdateOptions<T extends Record<string, any>> {
  /** Entity type for the mass update API */
  entityType: EntityType
  /** Grid context for cache updates */
  context: GridContext
  /** Current row selection model from the grid */
  rowSelectionModel: GridRowSelectionModel
  /** Grid API ref for direct row updates */
  apiRef: MutableRefObject<GridApiPremium>
  /** Fields that can be edited inline (whitelist for safety) */
  editableFields: string[]
  /**
   * Optional base processRowUpdate handler for single-row updates.
   * If not provided, uses the mass update API for single rows too.
   */
  baseProcessRowUpdate?: (newRow: T, oldRow: T) => Promise<T> | T
  /**
   * Optional callback when mass update completes
   */
  onMassUpdateComplete?: (count: number, field: string, value: any) => void
  /**
   * Optional resolver data for building display-ready grid updates
   * Pass users/contacts/organizations to resolve IDs to display objects
   */
  resolvers?: ResolverOptions
}

/**
 * Detect which fields changed between old and new row
 */
const getChangedFields = <T extends Record<string, any>>(
  newRow: T,
  oldRow: T,
  editableFields: string[]
): Record<string, any> => {
  const changes: Record<string, any> = {}

  for (const field of editableFields) {
    const newValue = getNestedValue(newRow, field)
    const oldValue = getNestedValue(oldRow, field)

    // Deep comparison for arrays/objects
    if (!isEqual(newValue, oldValue)) {
      changes[field] = newValue
    }
  }

  return changes
}

/**
 * Get value from object - supports both literal keys ('team.AM') and nested paths (team.AM)
 * First checks for literal key, then tries nested path
 */
const getNestedValue = (obj: Record<string, any>, path: string): any => {
  // First try literal key (e.g., obj['team.AM'])
  if (obj && path in obj) {
    return obj[path]
  }
  // Then try nested path (e.g., obj.team.AM)
  return path.split('.').reduce((current, key) => current?.[key], obj)
}

/**
 * Simple deep equality check for values
 */
const isEqual = (a: any, b: any): boolean => {
  if (a === b) return true
  if (a == null || b == null) return a === b
  if (typeof a !== typeof b) return false

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    return a.every((item, idx) => isEqual(item, b[idx]))
  }

  if (typeof a === 'object') {
    const keysA = Object.keys(a)
    const keysB = Object.keys(b)
    if (keysA.length !== keysB.length) return false
    return keysA.every(key => isEqual(a[key], b[key]))
  }

  return false
}

export const useInlineMassUpdate = <T extends Record<string, any>>(
  options: UseInlineMassUpdateOptions<T>
) => {
  const {
    entityType,
    context,
    rowSelectionModel,
    apiRef,
    editableFields,
    baseProcessRowUpdate,
    onMassUpdateComplete,
    resolvers,
  } = options

  const { showSnackbar } = useSnackbarContext()
  const { handleMassUpdate, isUpdating } = useMassUpdate({
    entityType,
    context,
    apiRef,
    resolvers,
  })

  // Track if we're currently processing to avoid duplicate calls
  const isProcessingRef = useRef(false)

  /**
   * Enhanced processRowUpdate that handles mass updates
   */
  const processRowUpdate = useCallback(
    async (newRow: T, oldRow: T): Promise<T> => {
      // Prevent duplicate processing
      if (isProcessingRef.current) {
        return newRow
      }

      const selectedCount = rowSelectionModel?.ids?.size ?? 0
      const changedFields = getChangedFields(newRow, oldRow, editableFields)
      const changedFieldNames = Object.keys(changedFields)

      // No changes detected
      if (changedFieldNames.length === 0) {
        return newRow
      }

      // Single row selected or no selection - use normal single-row update
      if (selectedCount <= 1) {
        if (baseProcessRowUpdate) {
          return baseProcessRowUpdate(newRow, oldRow)
        }
        // If no base handler, use mass update for single row too
        isProcessingRef.current = true
        try {
          // When no rows are selected, create a temporary selection with just the edited row
          const effectiveSelection: GridRowSelectionModel =
            selectedCount === 0
              ? {
                  ids: new Set([newRow.rowId || newRow.PK || newRow.id]),
                  type: 'include' as const,
                }
              : rowSelectionModel

          handleMassUpdate(changedFields, effectiveSelection)
          return newRow
        } finally {
          isProcessingRef.current = false
        }
      }

      // Multiple rows selected - mass update mode
      isProcessingRef.current = true

      try {
        // Show confirmation for mass updates affecting many rows
        const fieldName = changedFieldNames[0]
        const fieldValue = changedFields[fieldName]

        console.log(
          `[useInlineMassUpdate] Mass updating ${selectedCount} rows - field: ${fieldName}, value:`,
          fieldValue
        )

        // Trigger mass update for all selected rows
        handleMassUpdate(changedFields, rowSelectionModel)

        // Notify caller if callback provided
        if (onMassUpdateComplete) {
          onMassUpdateComplete(selectedCount, fieldName, fieldValue)
        }

        return newRow
      } catch (error) {
        console.error('[useInlineMassUpdate] Mass update failed:', error)
        showSnackbar('Mass update failed', 'error')
        // Return old row on error to revert the change
        return oldRow
      } finally {
        isProcessingRef.current = false
      }
    },
    [
      rowSelectionModel,
      editableFields,
      baseProcessRowUpdate,
      handleMassUpdate,
      onMassUpdateComplete,
      showSnackbar,
    ]
  )

  return {
    processRowUpdate,
    isUpdating,
  }
}

export default useInlineMassUpdate
